import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Spiner, InputForm } from '../../components';
import { dedupeRoleOptions, type InviteRoleLike } from '../../utils/inviteRoleOptions';

interface ChangePersonRoleModalProps {
    modalId?: string;
    personName?: string;
    currentRole?: string;
    availableRoles?: ReadonlyArray<InviteRoleLike> | null;
    isOpen?: boolean;
    onClose?: () => void;
    onSubmit: (role: string) => Promise<boolean | void> | boolean | void;
}

/**
 * Dark Bootstrap modal to move a contributor to another role (select existing OR type a new one).
 */
const ChangePersonRoleModal: React.FC<ChangePersonRoleModalProps> = ({
    modalId = 'changePersonRoleModal',
    personName,
    currentRole = '',
    availableRoles,
    isOpen,
    onClose,
    onSubmit,
}) => {
    const { t } = useTranslation();
    const closeRef = useRef<HTMLButtonElement>(null);
    const roleOptions = useMemo(() => dedupeRoleOptions(availableRoles), [availableRoles]);
    const [role, setRole] = useState(currentRole);
    const [isSubmit, setIsSubmit] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen) {
            setRole(currentRole || '');
            setError('');
            setIsSubmit(false);
            const timer = window.setTimeout(() => {
                const el = document.getElementById(modalId);
                if (!el) return;
                const Modal = (window as any).bootstrap?.Modal;
                if (Modal) {
                    Modal.getOrCreateInstance(el).show();
                } else {
                    const btn = document.createElement('button');
                    btn.setAttribute('data-bs-toggle', 'modal');
                    btn.setAttribute('data-bs-target', `#${modalId}`);
                    document.body.appendChild(btn);
                    btn.click();
                    btn.remove();
                }
            }, 30);
            return () => window.clearTimeout(timer);
        }
        return undefined;
    }, [isOpen, currentRole, modalId]);

    useEffect(() => {
        const el = document.getElementById(modalId);
        if (!el) return;
        const onHidden = () => {
            setError('');
            setIsSubmit(false);
            onClose?.();
        };
        el.addEventListener('hidden.bs.modal', onHidden);
        return () => el.removeEventListener('hidden.bs.modal', onHidden);
    }, [modalId, onClose]);

    const handleSubmit = async (event: React.SyntheticEvent) => {
        event.preventDefault();
        const next = role.trim();
        if (!next) {
            setError(t('navigation.selectRole'));
            return;
        }
        setIsSubmit(true);
        setError('');
        try {
            const result = await onSubmit(next);
            if (result === false) {
                setIsSubmit(false);
                return;
            }
            closeRef.current?.click();
        } catch {
            setError(t('views.processInterview.moveFailed', 'Could not move contributor.'));
        }
        setIsSubmit(false);
    };

    return (
        <div className="modal" data-bs-backdrop="static" id={modalId} tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content bg-dark">
                    <div className="modal-header border-0">
                        <h5 className="modal-title text-white">
                            {t('views.processInterview.changeRole', 'Change role')}
                        </h5>
                        <button
                            type="button"
                            className="btn-close bg-white"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                        />
                    </div>
                    <div className="modal-body">
                        {error ? (
                            <div className="alert alert-danger" role="alert">
                                {error}
                            </div>
                        ) : null}
                        {personName ? (
                            <p className="text-theme small mb-3">
                                {personName}
                                {currentRole ? (
                                    <>
                                        {' '}
                                        · <span className="text-secondary">{currentRole}</span>
                                    </>
                                ) : null}
                            </p>
                        ) : null}

                        {roleOptions.length > 0 ? (
                            <>
                                <div className="mb-3">
                                    <select
                                        className="form-select bg-dark text-theme py-3"
                                        value={
                                            roleOptions.some((o) => o.role === role) ? role : ''
                                        }
                                        onChange={(e) => setRole(e.target.value)}
                                        aria-label={t('navigation.selectRole')}
                                    >
                                        <option className="text-theme" value="">
                                            {t('navigation.selectRole')}
                                        </option>
                                        {roleOptions.map((option) => (
                                            <option key={option.role} value={option.role}>
                                                {option.role}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="d-flex align-items-center my-2 gap-1">
                                    <hr className="flex-grow-1 border-secondary" />
                                    <span className="mx-2 text-light">{t('words.or')}</span>
                                    <hr className="flex-grow-1 border-secondary" />
                                </div>
                            </>
                        ) : null}

                        <InputForm
                            inputType="text"
                            inputLabel={t('navigation.enterRole')}
                            inputName="change-role-label"
                            inputValue={role}
                            isFloatingLabel={true}
                            labelClass="text-theme"
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                setRole(e.target.value)
                            }
                            inputClass="bg-dark text-theme"
                        />
                    </div>
                    <div className="modal-footer border-secondary">
                        <button
                            ref={closeRef}
                            type="button"
                            className="btn btn-secondary"
                            data-bs-dismiss="modal"
                        >
                            {t('words.close')}
                        </button>
                        <button type="button" className="btn btn-primary" onClick={handleSubmit}>
                            {isSubmit ? <Spiner className="spinner-border-sm" /> : t('words.save')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChangePersonRoleModal;
